import { useState, useCallback } from 'react';
import { UI_CONFIG } from '../constants/config.js';

/**
 * Custom hook for managing snackbar/toast notifications
 *
 * @returns {Object} Snackbar state and controls
 * @property {Object} snackbar - Current snackbar state { message, visible, type }
 * @property {Function} showSnackbar - Show a snackbar with message and type
 * @property {Function} hideSnackbar - Manually hide the snackbar
 */
const useSnackbar = () => {
  const [snackbar, setSnackbar] = useState({
    message: '',
    visible: false,
    type: 'info', // 'info' | 'success' | 'warning' | 'error'
  });

  /**
   * Show a snackbar message
   * @param {string} message - The message to display
   * @param {string} type - The type of snackbar ('info', 'success', 'warning', 'error')
   * @param {number} duration - How long to show the snackbar (ms), defaults to 3000
   */
  const showSnackbar = useCallback((message, type = 'info', duration = UI_CONFIG.snackbarDuration) => {
    setSnackbar({ message, visible: true, type });

    // Auto-hide after duration
    setTimeout(() => {
      setSnackbar((prev) => ({ ...prev, visible: false }));
    }, duration);
  }, []);

  /**
   * Manually hide the snackbar
   */
  const hideSnackbar = useCallback(() => {
    setSnackbar((prev) => ({ ...prev, visible: false }));
  }, []);

  return {
    snackbar,
    showSnackbar,
    hideSnackbar,
  };
};

export default useSnackbar;
